/**
 * TemplateCategoryTab – Content panel for a single template category.
 *
 * Renders:
 * - Search bar + "Create new" button
 * - Template table (TemplateTable with proper Mantine Table)
 *
 * Interaction model (strict):
 * - Eye icon → Preview (full-screen drawer, editable, non-persistent)
 * - Click template name → Template Editor (full-screen drawer, persistent editing)
 * - "Create new" → Template Editor (full-screen drawer, new template)
 * - Delete → Delete confirmation (drawer, not modal)
 *
 * No modals are used. All overlays use full-screen drawers.
 * Data is loaded and persisted via Medplum (useTemplates + useTemplateCRUD).
 * TablePaginationFooter is always shown below the table (empty or loaded) for layout parity across tabs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconPlus } from '@tabler/icons-react';
import { TEMPLATE_CATEGORY_LABELS, type TemplateCategory, type TemplateListItem } from '../types';
import { useTemplates, useTemplateCRUD } from '../hooks';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { TemplateTable } from './TemplateTable';
import { TemplatePreviewDrawer } from './TemplatePreviewDrawer';
import { DeleteConfirmationDrawer } from './DeleteConfirmationModal';
import { isMockTemplateId, updateMockTemplateStatus } from '../mockTemplates';
import { SearchInput } from '../../../../components/SearchInput';
import { TablePaginationFooter } from '../../../../components/TablePaginationFooter';
import { isLocalTemplateId, saveLocalTemplate, deleteLocalTemplate } from '../localTemplateStore';
import {
  readTemplateLibraryRowsPerPage,
  writeTemplateLibraryRowsPerPage,
  type TemplateLibraryTab,
} from '../templateLibraryUiState';
import { validateTreatmentPlanDiagnosisSemantics } from '../utils/treatmentPlanTemplateValidation';
import { templateIdsForStatusToggle } from '../utils/templateListHelpers';
import { isStandardDiagnosisTreatmentPlanTemplate } from '../../../../lib/chart/chartDocumentTemplates';

interface TemplateCategoryTabProps {
  category: TemplateCategory;
  /** When this changes, this tab refetches (so all tabs can refresh after a save that moves a template). */
  refetchKey?: number;
  /** Called after a template is saved so the parent can bump refetchKey and refresh all tabs. */
  onTemplateSaved?: () => void;
}

const TEMPLATE_EDITOR_PATH = '/dashboard/settings/documentation/template';

export function TemplateCategoryTab({
  category,
  refetchKey = 0,
}: TemplateCategoryTabProps): JSX.Element {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const isUnifyAdmin = role === 'unify-admin';
  const [search, setSearch] = useState('');
  /** 1-based page; server offset = (page - 1) * pageSize. */
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(readTemplateLibraryRowsPerPage);
  const pageSize = parseInt(rowsPerPage, 10);

  const handleRowsPerPageChange = useCallback((value: string | null) => {
    if (!value) return;
    setRowsPerPage(value);
    writeTemplateLibraryRowsPerPage(value);
  }, []);

  // Preview drawer state (editable, non-persistent)
  const [previewOpened, setPreviewOpened] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateListItem | null>(null);

  // Delete confirmation drawer state
  const [deleteOpened, setDeleteOpened] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activateWarningOpened, setActivateWarningOpened] = useState(false);
  const [pendingActivate, setPendingActivate] = useState<{
    ids: string[];
    nextStatus: 'active' | 'draft';
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [statusById, setStatusById] = useState<Record<string, 'active' | 'draft'>>({});
  const [toggling, setToggling] = useState(false);

  const { templates, total, loading, refetch } = useTemplates({
    category,
    search: search.trim() || undefined,
    pageSize,
    page: page - 1,
    sortBy: 'title',
    sortOrder: 'asc',
  });
  const crud = useTemplateCRUD();

  /** Latest refetch without listing it as an effect dep — `refetch` changes every time `page` changes (via `doFetch` in `useTemplates`), which must not reset pagination. */
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  /** Medplum match count when available; else row count (e.g. local drafts only) so the footer stays accurate. */
  const paginationTotal = useMemo(
    () => (total > 0 ? total : templates.length),
    [total, templates.length],
  );

  const displayTemplates = useMemo(
    () =>
      templates.map((template) => {
        const status = statusById[template.id];
        if (!status || status === template.status) return template;
        return {
          ...template,
          status,
          resource: { ...template.resource, status },
        };
      }),
    [templates, statusById],
  );

  useEffect(() => {
    const visible = new Set(templates.map((t) => t.id));
    setSelectedIds((prev) => prev.filter((id) => visible.has(id)));
  }, [templates]);

  // Keep current page in range when total or page size changes (e.g. after refetch or filter).
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(paginationTotal / pageSize));
    setPage((p) => Math.min(p, maxPage));
  }, [paginationTotal, pageSize]);

  // New search → first page
  useEffect(() => {
    setPage(1);
  }, [search]);

  // When parent bumps refetchKey (e.g. after another tab saved and moved a template), refetch this tab's list.
  // Do not depend on `refetch` — its identity updates on every page change and would snap back to page 1.
  useEffect(() => {
    if (refetchKey === 0) return;
    setPage(1);
    void refetchRef.current();
  }, [refetchKey]);

  // --- Preview actions (non-persistent) ---

  const handlePreview = useCallback((template: TemplateListItem) => {
    setPreviewTemplate(template);
    setPreviewOpened(true);
  }, []);

  // --- Editor actions (persistent) ---

  /** Click template name → navigate to full-page Template Editor (pass mock template in state) */
  const handleClickName = useCallback(
    (template: TemplateListItem) => {
      if (isStandardDiagnosisTreatmentPlanTemplate(template.resource)) {
        handlePreview(template);
        return;
      }
      navigate(`${TEMPLATE_EDITOR_PATH}/${template.id}`, {
        state: {
          ...(isMockTemplateId(template.id) ? { template } : {}),
          returnTab: category as TemplateLibraryTab,
        },
      });
    },
    [navigate, category, handlePreview],
  );

  /** "Create new" → navigate to full-page Template Editor */
  const handleCreateNew = useCallback(() => {
    navigate(`${TEMPLATE_EDITOR_PATH}/new?category=${category}`, {
      state: { returnTab: category as TemplateLibraryTab },
    });
  }, [navigate, category]);

  const persistTemplateStatus = useCallback(
    async (template: TemplateListItem, nextStatus: 'active' | 'draft') => {
      if (isMockTemplateId(template.id)) {
        updateMockTemplateStatus(template.id, nextStatus);
        return;
      }
      if (isLocalTemplateId(template.id)) {
        saveLocalTemplate({
          ...template,
          status: nextStatus,
          resource: { ...template.resource, status: nextStatus },
        });
        return;
      }
      await crud.updateTemplate({
        ...template.resource,
        status: nextStatus,
      });
    },
    [crud],
  );

  const applyTemplateStatusChange = useCallback(
    async (ids: string[], nextStatus: 'active' | 'draft') => {
      const previous = Object.fromEntries(
        displayTemplates
          .filter((t) => ids.includes(t.id))
          .map((t) => [t.id, t.status === 'active' ? 'active' : 'draft'] as const),
      );
      setStatusById((prev) => {
        const next = { ...prev };
        for (const id of ids) next[id] = nextStatus;
        return next;
      });
      setToggling(true);
      try {
        const targets = displayTemplates.filter((t) => ids.includes(t.id));
        const results = await Promise.allSettled(
          targets.map((template) => persistTemplateStatus(template, nextStatus)),
        );
        const failedIds = targets
          .filter((_, index) => results[index]?.status === 'rejected')
          .map((t) => t.id);
        if (failedIds.length > 0) {
          setStatusById((prev) => {
            const next = { ...prev };
            for (const id of failedIds) {
              next[id] = previous[id] ?? next[id];
            }
            return next;
          });
        }
      } finally {
        setToggling(false);
      }
    },
    [displayTemplates, persistTemplateStatus],
  );

  const handleToggleActive = useCallback(
    (template: TemplateListItem) => {
      const nextStatus = template.status === 'active' ? 'draft' : 'active';
      const ids = templateIdsForStatusToggle(template.id, selectedIds);
      if (nextStatus === 'draft' && ids.length > 1) {
        const confirmed = window.confirm(`Turn off ${ids.length} selected templates?`);
        if (!confirmed) return;
      }
      if (nextStatus === 'active') {
        const targets = displayTemplates.filter((t) => ids.includes(t.id));
        if (targets.some((t) => validateTreatmentPlanDiagnosisSemantics(t.resource).length > 0)) {
          setPendingActivate({ ids, nextStatus });
          setActivateWarningOpened(true);
          return;
        }
      }
      void applyTemplateStatusChange(ids, nextStatus);
    },
    [applyTemplateStatusChange, displayTemplates, selectedIds],
  );

  const handleConfirmActivateAnyway = useCallback(async () => {
    if (!pendingActivate) return;
    await applyTemplateStatusChange(pendingActivate.ids, pendingActivate.nextStatus);
    setActivateWarningOpened(false);
    setPendingActivate(null);
  }, [applyTemplateStatusChange, pendingActivate]);

  const handleDuplicate = useCallback(
    async (template: TemplateListItem) => {
      setPreviewOpened(false);
      if (isMockTemplateId(template.id) || isLocalTemplateId(template.id)) {
        const copy: TemplateListItem = {
          ...template,
          id: `local-${Date.now()}`,
          title: `${template.title} (Copy)`,
          status: 'draft',
          lastUpdated: new Date().toISOString(),
          resource: { ...template.resource, id: undefined, title: `${template.title} (Copy)`, status: 'draft' },
        };
        saveLocalTemplate(copy);
        refetch();
        return;
      }
      try {
        await crud.duplicateTemplate(template.id);
        refetch();
      } catch {
        notify({
          type: 'error',
          message: 'Could not duplicate template. Try again or contact Unify support.',
        });
      }
    },
    [crud, refetch],
  );

  const handleArchive = useCallback(
    async (template: TemplateListItem) => {
      setPreviewOpened(false);
      if (isMockTemplateId(template.id)) {
        updateMockTemplateStatus(template.id, 'retired' as 'draft' | 'active');
        refetch();
        return;
      }
      if (isLocalTemplateId(template.id)) {
        saveLocalTemplate({ ...template, status: 'retired' as TemplateListItem['status'], resource: { ...template.resource, status: 'retired' } });
        refetch();
        return;
      }
      try {
        await crud.archiveTemplate(template.id);
        refetch();
      } catch {
        // Silently fail for server errors
      }
    },
    [crud, refetch],
  );

  const handleDeleteRequest = useCallback((template: TemplateListItem) => {
    setDeletingTemplate(template);
    setDeleteOpened(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingTemplate) return;
    setDeleting(true);
    try {
      if (isMockTemplateId(deletingTemplate.id)) {
        // Mock templates can't truly be deleted; just close the dialog
      } else if (isLocalTemplateId(deletingTemplate.id)) {
        deleteLocalTemplate(deletingTemplate.id);
      } else {
        await crud.deleteTemplate(deletingTemplate.id);
      }
      refetch();
      setDeleteOpened(false);
      setDeletingTemplate(null);
    } catch {
      // Silently fail for server errors
    } finally {
      setDeleting(false);
    }
  }, [crud, deletingTemplate, refetch]);

  return (
    <Stack gap="md">
      {/* Search + Create */}
      <Group justify="space-between">
        <SearchInput
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        {isUnifyAdmin && (
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateNew}>
            Create new
          </Button>
        )}
      </Group>

      {/* Template list (proper Mantine Table inside Paper) */}
      <TemplateTable
        templates={displayTemplates}
        onPreview={handlePreview}
        onDuplicate={handleDuplicate}
        onDelete={handleDeleteRequest}
        onToggleActive={handleToggleActive}
        onClickName={handleClickName}
        canManageTemplates={isUnifyAdmin}
        isTemplateLocked={(t) => isStandardDiagnosisTreatmentPlanTemplate(t.resource)}
        loading={loading}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        toggling={toggling}
      />

      <TablePaginationFooter
        totalItems={paginationTotal}
        currentPage={page}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        aria-label={`${TEMPLATE_CATEGORY_LABELS[category]} templates pagination`}
      />

      {/* Preview drawer (editable, non-persistent) */}
      <TemplatePreviewDrawer
        opened={previewOpened}
        onClose={() => {
          setPreviewOpened(false);
          setPreviewTemplate(null);
        }}
        template={previewTemplate}
        onDuplicate={isUnifyAdmin ? handleDuplicate : undefined}
        onArchive={isUnifyAdmin ? handleArchive : undefined}
      />

      {/* Delete confirmation drawer */}
      <DeleteConfirmationDrawer
        opened={deleteOpened}
        onClose={() => {
          setDeleteOpened(false);
          setDeletingTemplate(null);
        }}
        template={deletingTemplate}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
      />

      <Modal
        opened={activateWarningOpened}
        onClose={() => {
          setActivateWarningOpened(false);
          setPendingActivate(null);
        }}
        title="Activate treatment plan template?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This treatment plan template has no diagnosis code field. Diagnosis will not sync to the
            episode of care or billing. Add a question with linkId suffix <code>-diagnosis-code</code>.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setActivateWarningOpened(false);
                setPendingActivate(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleConfirmActivateAnyway()}>
              Activate anyway
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
